import type { DisplayFactor } from '@parlay-picker/shared/scoreDisplay';
import { shortStatLabel } from '@parlay-picker/shared/statLabels';
import { formatFactorName } from '@utils/formatFactorName';
import type React from 'react';
import { formatLegLabel, stripPlayerPrefix } from './scorePanelUtils';

/**
 * Single factor display row (with optional leg label)
 */
export function FactorChip({ factor }: { factor: DisplayFactor }): React.ReactElement {
  const bgColor =
    factor.impact === 'positive'
      ? 'bg-theme-success/15 border-theme-success/30'
      : factor.impact === 'negative'
        ? 'bg-theme-danger/15 border-theme-danger/30'
        : 'bg-theme-surface border-theme-border';

  const textColor =
    factor.impact === 'positive'
      ? 'text-theme-success'
      : factor.impact === 'negative'
        ? 'text-theme-danger'
        : 'text-theme-text-secondary';

  const isLegSpecific = factor.legIndex !== undefined;
  const legLabel = formatLegLabel(factor.playerName, factor.propType);

  const fullText = factor.factor + (factor.details ? ` — ${factor.details}` : '');

  return (
    <div
      className={`flex items-center justify-between px-2 py-1.5 rounded border ${bgColor}`}
      title={fullText}
    >
      <div className="flex flex-col gap-0.5 min-w-0">
        <div className="flex items-center gap-1.5 min-w-0">
          {isLegSpecific && legLabel && (
            <span
              className="text-[9px] px-1 py-0.5 rounded bg-theme-surface text-theme-text-secondary font-medium whitespace-nowrap shrink-0"
              title={`${factor.playerName}${factor.propType ? ` - ${shortStatLabel(factor.propType)}` : ''}`}
            >
              {legLabel}
            </span>
          )}
          <span className="text-xs text-theme-text truncate">
            {formatFactorName(factor.factor)}
          </span>
        </div>
        {factor.details && (
          <span className="text-[10px] text-theme-text-tertiary truncate">{factor.details}</span>
        )}
      </div>
      <div className="flex items-center gap-2">
        {factor.winRate != null && (
          <span className="text-[10px] text-theme-text-tertiary">{factor.winRate.toFixed(0)}%</span>
        )}
        {factor.sampleSize != null && (
          <span className="text-[10px] text-theme-text-tertiary">n={factor.sampleSize}</span>
        )}
        {factor.impact === 'neutral' ? (
          <span className="text-xs text-theme-text-tertiary" title="Context only - no score impact">
            ℹ️
          </span>
        ) : (
          <span className={`text-xs font-bold ${textColor}`}>
            {factor.displayScore > 0 ? '+' : ''}
            {factor.displayScore}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Compact factor display for leg-grouped view (no leg label since shown in header)
 */
export function LegFactorChip({ factor }: { factor: DisplayFactor }): React.ReactElement {
  const bgColor =
    factor.impact === 'positive'
      ? 'bg-theme-success/15 border-theme-success/30'
      : factor.impact === 'negative'
        ? 'bg-theme-danger/15 border-theme-danger/30'
        : 'bg-theme-surface/50 border-theme-border/50';

  const textColor =
    factor.impact === 'positive'
      ? 'text-theme-success'
      : factor.impact === 'negative'
        ? 'text-theme-danger'
        : 'text-theme-text-secondary';

  const displayText = stripPlayerPrefix(factor.factor, factor.playerName);
  const fullText = factor.factor + (factor.details ? ` — ${factor.details}` : '');

  return (
    <div
      className={`flex items-center justify-between px-2 py-1 rounded border ${bgColor}`}
      title={fullText}
    >
      <div className="flex flex-col gap-0.5 flex-1 min-w-0">
        <span className="text-[11px] text-theme-text truncate">{displayText}</span>
        {factor.details && (
          <span className="text-[9px] text-theme-text-tertiary truncate">{factor.details}</span>
        )}
      </div>
      <div className="flex items-center gap-1.5 ml-2 shrink-0">
        {factor.winRate != null && (
          <span className="text-[9px] text-theme-text-tertiary">{factor.winRate.toFixed(0)}%</span>
        )}
        {factor.sampleSize != null && (
          <span className="text-[9px] text-theme-text-tertiary">n={factor.sampleSize}</span>
        )}
        {factor.impact === 'neutral' ? (
          <span
            className="text-[10px] text-theme-text-tertiary"
            title="Context only - no score impact"
          >
            ℹ️
          </span>
        ) : (
          <span className={`text-[11px] font-bold ${textColor}`}>
            {factor.displayScore > 0 ? '+' : ''}
            {factor.displayScore}
          </span>
        )}
      </div>
    </div>
  );
}
